import { getLocalStorage, removeLocalStorage } from "@shared/lib";
import { setIsInitializingApp } from "@app/store/appSlice";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

interface Entity {
  id: string;
}

interface UseNavigateToEntityConfig<T extends Entity> {
  rootPath: string;
  lastVisitedKey: string;
  findEntityByPath: (entities: T[], path?: string) => T | null | undefined;
}

const useNavigateToEntity = <T extends Entity>(
  config: UseNavigateToEntityConfig<T>
) => {
  const { rootPath, lastVisitedKey, findEntityByPath } = config;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();

  const gotoEntityPath = (
    entity: T,
    pathToRedirect: string = pathname + search
  ) => {
    /**
     * if path has entity id then goto path, 
     * but there could be another case (|| case) also where user is refreshing the page while he/she was in user's page
       or any other page other then entity but that path should not be simply /, then also simply goto that path
    *  */
    dispatch(setIsInitializingApp(false));

    if (
      pathToRedirect.startsWith(rootPath) ||
      pathToRedirect === "/"
    ) {
      if (pathToRedirect.includes(entity.id)) {
        navigate(pathToRedirect);
      } else {
        navigate(`${rootPath}/${entity.id}`);
      }
    }
  };

  /**
   * Cases in mind while implementation
   * 1. someone redirected to entity and he/she was not logged in, in that case we will have __REDIRECT_PATH, so want to redirect to same path after login,
   * But here after login we are first checking if entity in the path still has permission or not
   *
   * 2. was already loggedIn, just refresh the page, but we check the permissions and then if user has permission to the entity then redirecting to same path
   *
   */
  const navigateToEntity = (entities: T[] = []) => {
    if (entities.length === 0) {
      return;
    }
    const redirectPathBeforeLogin = getLocalStorage(
      "__REDIRECT_PATH"
    ) as string;
    const lastEntityId = getLocalStorage(lastVisitedKey) as string;
    const entityFromPath = findEntityByPath(entities, redirectPathBeforeLogin);
    removeLocalStorage("__REDIRECT_PATH");

    if (redirectPathBeforeLogin && entityFromPath) {
      // Case: user just got loggedIn
      gotoEntityPath(entityFromPath, redirectPathBeforeLogin);
    } else if (entities?.length === 1) {
      gotoEntityPath(entities[0]);
    } else if (lastEntityId) {
      const lastNavEntity = entities?.find((e) => e.id === lastEntityId);
      if (lastNavEntity) {
        gotoEntityPath(lastNavEntity);
      } else {
        /**
         * Case where you don't have access to last visited entity now
         */
        dispatch(setIsInitializingApp(false));
        navigate(rootPath);
      }
    } else {
      dispatch(setIsInitializingApp(false));
      navigate(rootPath);
    }
  };

  return { navigateToEntity };
};

export default useNavigateToEntity;
